// inv/src/pages/Blog.jsx
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Rss } from 'lucide-react';
import { useInitialData } from '../components/data/DataContext';
import SeoHead from '../seo/seoHead';
import Breadcrumbs from '../seo/breadcrumbs';
import Pagination from '../components/ui/pagination';
import languages from '../locales/languages';
import { API_URL } from '../config';
import { Badge } from "../components/ui/badge";
import CTAHover from '../components/ui/CTAHover';

const parseDate = (value) => {
  if (!value) return null;

  let raw = value;
  if (typeof value === 'object' && value && '$date' in value) {
    const v = value.$date;
    raw = (typeof v === 'object' && v && '$numberLong' in v) ? Number(v.$numberLong) : v;
  }

  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

const formatUTCDate = (value, locale = 'en-US') => {
  const d = parseDate(value);
  if (!d) return '';
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(d);
};

const Blog = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { articles = [], total = 0, seo = {} } = useInitialData();

  const page = Number(searchParams.get('p') || 1);
  const limit = 8;

  const [articlesState, setArticles] = useState(articles);
  const [totalState, setTotal] = useState(total);

  const {
    title,
    description,
    canonicalUrl,
    ogTitle,
    ogDescription,
    ogImage,
    ogUrl,
    ogType,
    twitterCard,
    prevUrl,
    nextUrl,
    alternates,
  } = seo;

  const langFromUrl = location.pathname.split('/').filter(Boolean)[0];
  const currentLang = languages.some(l => l.code === langFromUrl) ? langFromUrl : 'en';
  const prefix = currentLang === 'en' ? '' : `/${currentLang}`;

  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      if (page === 1 && articlesState.length) return;
    }

    const fetchArticles = async () => {
      try {
        const res = await fetch(`${API_URL}/articles-cdn?limit=${limit}&page=${page}`);
        const data = await res.json();
        setArticles(data.articles || []);
        setTotal(data.total || 0);
      } catch (error) {
        console.error('Error fetching articles:', error);
      }
    };

    fetchArticles();
  }, [page]);

  const firstPart = articlesState.slice(0, 4);
  const secondPart = articlesState.slice(4);
  const totalPages = Math.ceil(totalState / limit);

  const changePage = (newPage) => {
    if (newPage === page) return;
    const params = {};
    if (newPage !== 1) params.p = newPage;
    setSearchParams(params, { replace: true });
  };

  return (
    <>
      <SeoHead
        lang={currentLang}
        title={title}
        description={description}
        canonicalUrl={canonicalUrl}
        ogTitle={ogTitle}
        ogDescription={ogDescription}
        ogImage={ogImage}
        ogUrl={ogUrl}
        ogType={ogType}
        twitterCard={twitterCard}
        prevUrl={prevUrl}
        nextUrl={nextUrl}
        alternates={alternates}
        jsonLd={seo.jsonLd}
      />

      <div className="mx-auto max-w-7xl px-8 lg:px-0 my-8">
        <Breadcrumbs mapSegments={{ lists: t('menu.blog') }} pageTitle={t('menu.blog')} />

        <div className="flex items-center justify-start mb-8">
          <Rss className="w-6 h-6 mr-2" />
          <div className="text-4xl font-bold">{t('menu.blog')}</div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {firstPart.map(a => <ArticleCard key={a._id} article={a} prefix={prefix} />)}

          <PromoCard t={t} />

          {secondPart.map(a => <ArticleCard key={a._id} article={a} prefix={prefix} />)}
        </div>

        {totalPages > 1 && (
          <div className="mt-8 py-8 flex justify-center">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={changePage}
            />
          </div>
        )}
      </div>
    </>
  );
};

const ArticleCard = ({ article, prefix }) => {
  const dateText = formatUTCDate(article.publish_date, 'en-US');

  return (
    <div className="rounded-xl overflow-hidden flex flex-col border border-gray-200 hover:shadow-lg transition-shadow h-full">
      <Link to={`${prefix}/blog/${article.url_slug}/`} className="flex flex-col h-full">
        <div className="relative w-full h-60 overflow-hidden">
          {article.img_url && (
            <img src={article.img_url} alt={article.title} />
          )}
        </div>
        <div className="relative flex flex-col flex-grow p-4 justify-between">
          <div className="flex-grow">
            <h2 className="text-lg font-semibold mb-2">{article.title}</h2>
            <p className="text-sm text-stone-800 ">{article.short_description}</p>
          </div>
          <div className="mt-4">
            {dateText && <Badge text={dateText} variant="v6" />}
          </div>
        </div>
      </Link>
    </div>
  );
};

const PromoCard = ({ t }) => (
  <div className="rounded-xl overflow-hidden relative h-full flex flex-col justify-end">
    <div className="bg-gradient-to-r from-neutral-100 to-orange-50 p-4 text-stone-900 h-full flex flex-col justify-center">
      <h3 className="text-center text-2xl font-semibold mb-2">{t('other.t1')}</h3>
      <p className="text-center text-sm mb-4">{t('other.t2')}</p>
      <CTAHover
        className="mt-6"
        text={t('other.t3')}
        href="find-investors"
        align="center"
        bgColor="bg-blue-600 text-white"
        textColor="text-stone-700"
        hoverTextColor="text-white"
      />
    </div>
  </div>
);

export default Blog;
